import { GuestLocationHint } from './components/guest-location-hint';
import { guestLocationHintDismissedAtom } from 'app/atoms/guest-session';
import {
  presentGuestSignupPromptAtom,
  footerMarginBottomAtom,
  isLoggedInAtom,
  searchObjectQuery,
  showSearchBar,
} from 'app/atoms';
import { useListingCategoriesEnabled } from 'app/hooks/use-listing-categories';
import { useListingCatalog } from 'app/hooks/use-listing-catalog';
import { useFocusEffect } from '@react-navigation/native';
import { useHeaderHeight } from '@react-navigation/elements';
import { withErrorBoundary } from 'app/components/error-boundary';
import { Popper } from 'app/components/popper';
import { Column, Row } from 'app/design/primitives/box';
import { Button } from 'app/design/primitives/button';
import { SearchInput } from 'app/design/primitives/input/search';
import { Text } from 'app/design/primitives/text';
import { useClickOutside } from 'app/hooks/use-click-outside';
import { type NavigationScreen, type SpecificNavigationProp } from 'app/types/navigation';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import React, { useCallback, useEffect, useEffectEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { IconChevronDown, IconChevronUp, IconMap, IconMapPin } from '@tabler/icons-react-native';
import { useNavigation, useRoute } from 'app/hooks/navigation';
import { withDashboardLayout } from 'app/hooks/with-dashboard-layout';
import { getI18nLanguage } from 'app/lib/language';
import { useAllCategories } from 'app/network/category';
import { useReverseGeocodedAddress } from 'app/network/address';
import { type Tag, useAllTags, useDefaultListingLocation } from 'app/network/listing';
import { useUser } from 'app/network/user';
import { createTheme as createDSTheme } from 'app/shopify';
import { type Listing, type ListingsSortBy, ListingSubtype, ListingType, TransactionMode } from 'app/types/listing';
import type { Category } from 'app/types/category';
import type { FilterPreferences } from 'app/types/listings-filters';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';
import { DynamicFeatureFlag, isFeatureEnabled } from 'app/utils/helpers';
import { withOptionalMetrics } from 'app/utils/performance/with-metrics';
import { isMobileNative, useIsGuestWeb } from 'app/utils/web-helpers';
import { useClickOutside as useRnClickOutside } from 'react-native-click-outside';
import { useWindowDimensions, View } from 'react-native';
import { ScrollView } from 'react-native-gesture-handler';
import { ListingMap, useListingMapState, type ListingMapCatalogSync } from './components/listing-map';
import { GuestAnnouncementBanner } from './components/guest-announcement-banner';
import { GuestLocationFilterContent } from './components/guest-location-filter-content';
import { ListingsFiltersPanel } from './components/listings-filters';
import { RenderedList } from './components/rendered-list';
import { SearchPeopleResults } from './components/search-people-results';
import { SortDropdown } from './logged-in/sort-dropdown';
import { type AllListingsTab, TabSelector } from './logged-in/tab';
import { useAllListingsScreen } from './use-all-listings-screen';
import { FooterWithoutLinks } from 'app/components/web/explorer-layout';
import { CATALOG_GRID_MAX_WIDTH } from 'app/components/web/dashboard-layout-constants';
const theme = createDSTheme();

const peopleTab = 'people';

const AllListingsScreenComponent: NavigationScreen<'all-listings'> = ({ navigation, route }) => {
  useEffect(() => {
    if (!route.params?.type) {
      navigation.setParams({ activeTab: route.params?.activeTab ?? ListingType.offer, type: ListingType.offer });
    }
  }, [navigation, route.params?.activeTab, route.params?.type]);
  return <ContentWithParams navigation={navigation} />;
};

const AllListingsScreenWithProviders = withOptionalMetrics(
  'AllListingsScreen',
  withErrorBoundary(withDashboardLayout(AllListingsScreenComponent, 'explorer', true, { fullWidth: true })),
);

export function AllListingsScreen(props: React.ComponentProps<NavigationScreen<'all-listings'>>) {
  return <AllListingsScreenWithProviders {...props} />;
}

const ContentWithParams = ({ navigation }: { navigation: SpecificNavigationProp<'all-listings'> }) => {
  const navigationHeaderHeight = useHeaderHeight();
  const { height: windowHeight } = useWindowDimensions();
  const screenHeight = Math.max(0, windowHeight - navigationHeaderHeight);
  const [topContentHeight, setTopContentHeight] = React.useState(0);
  const route = useRoute<'all-listings'>();
  const setFooterMargin = useSetAtom(footerMarginBottomAtom);
  const [hoveredListingState, setHoveredListingState] = React.useState<{
    filterScope: object;
    listing: Listing;
  }>();
  const [mapListingCounts, setMapListingCounts] = React.useState<Partial<Record<ListingType, number>>>({});
  const [showMapPanel, setShowMapPanel] = React.useState(false);
  const { data: currentUser, isPending: isUserLoading } = useUser();
  const isGuestWeb = useIsGuestWeb();

  const type = route.params?.type ?? ListingType.offer;
  const {
    t,
    appliedFilters,
    offersTotal,
    requestsTotal,
    canSearchPeople,
    peopleQuery,
    showPeopleGroups,
    activeCategory,
    activeTag,
    currentFilters,
    forcedFilters,
    guestLocationFilters,
    sortOptions,
    search,
    isLoggedIn,
  } = useAllListingsScreen({ navigation, route });
  const filterKey = JSON.stringify(currentFilters);
  const filterScope = React.useMemo(() => ({ filterKey }), [filterKey]);
  const hoveredListing = hoveredListingState?.filterScope === filterScope ? hoveredListingState.listing : undefined;
  const setHoveredListing = useCallback<React.Dispatch<React.SetStateAction<Listing | undefined>>>(
    (nextListing) => {
      setHoveredListingState((previousState) => {
        const previousListing = previousState?.filterScope === filterScope ? previousState.listing : undefined;
        const listing = typeof nextListing === 'function' ? nextListing(previousListing) : nextListing;
        return listing ? { filterScope, listing } : undefined;
      });
    },
    [filterScope],
  );

  const canSearchCatalogOnMap =
    isLoggedIn && !isUserLoading && isFeatureEnabled(DynamicFeatureFlag.SearchCatalogOnMap, currentUser?.featureFlags);
  const shouldShowPeopleTab = isLoggedIn && search.trim().length >= 1;
  const activeTab: AllListingsTab = route.params?.activeTab ?? type;
  const displayedPeopleTotal = canSearchPeople ? peopleQuery.data?.pages[0]?.total : undefined;

  useEffect(() => {
    if (!shouldShowPeopleTab && activeTab === peopleTab) {
      navigation.setParams({ activeTab: type });
    }
  }, [activeTab, navigation, shouldShowPeopleTab, type]);

  useEffect(() => {
    setFooterMargin(80);
    return () => {
      //
    };
  }, [setFooterMargin]);

  const displayedTab = shouldShowPeopleTab ? activeTab : type;
  const shouldShowMapPanel = displayedTab !== peopleTab && canSearchCatalogOnMap && showMapPanel;

  const resetMapInteraction = useEffectEvent(() => {
    setHoveredListing(undefined);
    if (displayedTab === peopleTab || !isLoggedIn) {
      setMapListingCounts({});
    }
  });
  useEffect(() => {
    const timeout = setTimeout(resetMapInteraction, 0);
    return () => clearTimeout(timeout);
  }, [displayedTab, isLoggedIn]);

  const mapState = useListingMapState({
    appliedFilters,
    forcedFilters,
    highlightedListingId: hoveredListing?.id,
    isLoggedIn,
    listingType: type,
    navigation,
    onMarkerHoverChange: setHoveredListing,
    search,
    enabled: shouldShowMapPanel,
  });
  const mapCatalogSync = mapState.catalog;
  const displayedMapListingCounts = shouldShowMapPanel
    ? { ...mapListingCounts, [type]: mapCatalogSync?.listings?.length }
    : mapListingCounts;

  const handleTabChange = (tab: AllListingsTab) => {
    if (shouldShowMapPanel) {
      setMapListingCounts((previous) => ({ ...previous, [type]: mapCatalogSync?.listings?.length }));
    }
    if (tab === peopleTab) mapState.resetMap();
    if (tab !== peopleTab) {
      navigation.setParams({ activeTab: tab, type: tab });
    } else {
      navigation.setParams({ activeTab: tab });
    }
  };

  const handleMapPanelChange: React.Dispatch<React.SetStateAction<boolean>> = (next) => {
    const nextValue = typeof next === 'function' ? next(showMapPanel) : next;
    if (!nextValue) mapState.resetMap();
    setShowMapPanel(nextValue);
  };

  const handleSort = (by: ListingsSortBy) => {
    if (isGuestWeb) {
      navigation.setParams({ sort: by });
    }
    if (type === ListingType.offer) {
      appliedFilters.sortOffersState[1](by);
    } else {
      appliedFilters.sortNeedsState[1](by);
    }
  };

  return (
    <Column alignSelf="center" width="100%" style={{ height: screenHeight, maxHeight: screenHeight, minHeight: 0 }}>
      <CatalogFiltersHeader
        setTopContentHeight={setTopContentHeight}
        isGuestWeb={isGuestWeb}
        isLoggedIn={isLoggedIn}
        activeCategory={activeCategory}
        activeTag={activeTag}
        appliedFilters={appliedFilters}
        displayedTab={displayedTab}
        type={type}
        navigation={navigation}
        shouldShowMapPanel={shouldShowMapPanel}
        mapListingCounts={displayedMapListingCounts}
        offersTotal={offersTotal}
        handleSort={handleSort}
        handleTabChange={handleTabChange}
        displayedPeopleTotal={displayedPeopleTotal}
        requestsTotal={requestsTotal}
        shouldShowPeopleTab={shouldShowPeopleTab}
        currentFilters={currentFilters}
        sortOptions={sortOptions}
        canSearchCatalogOnMap={canSearchCatalogOnMap}
        setShowMapPanel={handleMapPanelChange}
        setHoveredListing={setHoveredListing}
        showMapPanel={showMapPanel}
        t={t}
      />

      <Row
        alignItems="stretch"
        gap="spacing.general.x4"
        paddingRight={shouldShowMapPanel ? 'spacing.general.x4' : undefined}
        style={{ height: Math.max(0, screenHeight - topContentHeight), minHeight: 0, position: 'relative', zIndex: 0 }}
      >
        <CatalogResults
          displayedTab={displayedTab}
          appliedFilters={appliedFilters}
          isLoggedIn={isLoggedIn}
          navigation={navigation}
          canSearchPeople={canSearchPeople}
          peopleQuery={peopleQuery}
          showPeopleGroups={showPeopleGroups}
          search={search}
          forcedFilters={forcedFilters}
          guestLocationFilters={guestLocationFilters}
          shouldShowMapPanel={shouldShowMapPanel}
          mapCatalogSync={mapCatalogSync}
          hoveredListing={hoveredListing}
          setHoveredListing={setHoveredListing}
        />
        {shouldShowMapPanel ? <ListingMap state={mapState} /> : null}
      </Row>
    </Column>
  );
};

interface DesktopFiltersSubheaderProps {
  activeCategory?: Category;
  activeTag?: Tag;
  appliedFilters?: FilterPreferences;
  currentTab: AllListingsTab;
  listingType: ListingType;
  navigation: SpecificNavigationProp<'all-listings'>;
  offersTotal: number;
  onSort: (sort: ListingsSortBy) => void;
  onTabChange: (tab: AllListingsTab) => void;
  peopleTotal: number | undefined;
  requestsTotal: number;
  showPeopleTab: boolean;
  sortBy: ListingsSortBy;
  sortOptions: ListingsSortBy[];
  trailing?: React.ReactNode;
}

function CatalogResults({
  displayedTab,
  appliedFilters,
  isLoggedIn,
  navigation,
  canSearchPeople,
  peopleQuery,
  showPeopleGroups,
  search,
  forcedFilters,
  guestLocationFilters,
  shouldShowMapPanel,
  mapCatalogSync,
  hoveredListing,
  setHoveredListing,
}: {
  displayedTab: AllListingsTab;
  appliedFilters: FilterPreferences;
  isLoggedIn: boolean;
  navigation: SpecificNavigationProp<'all-listings'>;
  canSearchPeople: boolean;
  peopleQuery: ReturnType<typeof useAllListingsScreen>['peopleQuery'];
  showPeopleGroups: boolean;
  search: string;
  forcedFilters: ReturnType<typeof useAllListingsScreen>['forcedFilters'];
  guestLocationFilters:
    | { searchCenterLatitude: number; searchCenterLongitude: number; searchDistance: number | undefined }
    | { searchCenterLatitude?: undefined; searchCenterLongitude?: undefined; searchDistance?: undefined };
  shouldShowMapPanel: boolean;
  mapCatalogSync: ListingMapCatalogSync | undefined;
  hoveredListing: Listing | undefined;
  setHoveredListing: React.Dispatch<React.SetStateAction<Listing | undefined>>;
}) {
  return (
    <Column flex={1} minWidth={420}>
      {displayedTab === peopleTab ? (
        <SearchPeopleResults
          addresses={appliedFilters.availableValues.addresses}
          groups={appliedFilters.availableValues.groups}
          isLoggedIn={isLoggedIn}
          layout="card"
          navigation={navigation}
          canSearchPeople={canSearchPeople}
          peopleQuery={peopleQuery}
          showGroups={showPeopleGroups}
          search={search}
          selectedAddresses={appliedFilters.selectedAddresses}
          selectedGroups={appliedFilters.selectedGroups}
        />
      ) : (
        <RenderedList
          additionalFooter={
            <View style={{ marginHorizontal: -theme.spacing['spacing.general.x4'] }}>
              <FooterWithoutLinks />
            </View>
          }
          appliedFilters={appliedFilters}
          listFormat="grid"
          showRequestBanner={false}
          search={search}
          allowSort={false}

          setSearch={() => {}}
          forcedFilters={forcedFilters}
          filterOverrides={guestLocationFilters}
          isLoggedIn={isLoggedIn}
          childBelowFilters={undefined}
          hideFilterButton
          withPadding // Gutters belong inside the scroll view, leaving its scrollbar at the edge.
          showVerticalScrollIndicator
          listingIds={shouldShowMapPanel ? mapCatalogSync?.listingIds : undefined}
          listingUserId={shouldShowMapPanel ? mapCatalogSync?.userId : undefined}
          navigation={navigation}
          showHighestCard
          highlightedListingId={shouldShowMapPanel ? hoveredListing?.id : undefined}
          onListingHoverChange={shouldShowMapPanel ? setHoveredListing : undefined}
        />
      )}
    </Column>
  );
}

function CatalogFiltersHeader({
  setTopContentHeight,
  isGuestWeb,
  isLoggedIn,
  activeCategory,
  activeTag,
  appliedFilters,
  displayedTab,
  type,
  navigation,
  shouldShowMapPanel,
  mapListingCounts,
  offersTotal,
  handleSort,
  handleTabChange,
  displayedPeopleTotal,
  requestsTotal,
  shouldShowPeopleTab,
  currentFilters,
  sortOptions,
  canSearchCatalogOnMap,
  setShowMapPanel,
  setHoveredListing,
  showMapPanel,
  t,
}: {
  setTopContentHeight: React.Dispatch<React.SetStateAction<number>>;
  isGuestWeb: boolean;
  isLoggedIn: boolean;
  activeCategory:
    | {
        popularItems?:
          | {
              pictureUrl?: string | undefined;
              selected?: boolean | undefined;
              id: string;
              categoryId: string;
              fr: string;
              en: string;
              es: string;
              popularItemList: {
                fr?: string | undefined;
                en?: string | undefined;
                es?: string | undefined;
                color?: string | undefined;
              };
            }[]
          | undefined;
        id: string;
        name: { fr: string; en: string; es: string };
        icon: string;
        colorDark: string;
        colorMedium: string;
        colorLight: string;
      }
    | undefined;
  activeTag: Tag | undefined;
  appliedFilters: FilterPreferences;
  displayedTab: AllListingsTab;
  type: ListingType;
  navigation: SpecificNavigationProp<'all-listings'>;
  shouldShowMapPanel: boolean;
  mapListingCounts: Partial<Record<ListingType, number>>;
  offersTotal: number;
  handleSort: (by: ListingsSortBy) => void;
  handleTabChange: (tab: AllListingsTab) => void;
  displayedPeopleTotal: number | undefined;
  requestsTotal: number;
  shouldShowPeopleTab: boolean;
  currentFilters: {
    searchCenterLatitude?: number | undefined;
    searchCenterLongitude?: number | undefined;
    searchDistance?: number | undefined;
    sortBy: ListingsSortBy;
    isLoggedIn: boolean;
    transactionModes?: TransactionMode[] | undefined;
    category: string | undefined;
    serviceCategory: string | undefined;
    tags: string[] | undefined;
    search: string;
    type: ListingType;
    subtypes: NonNullable<ListingSubtype | undefined>[];
    hideFoundRequests: boolean | undefined;
    dismissedOnly: boolean | undefined;
    hideOffersWithFee: boolean | undefined;
    groupIdsFilterActive?: true | undefined;
    addressIds: string[] | undefined;
    groupIds: string[] | undefined;
  };
  sortOptions: ListingsSortBy[];
  canSearchCatalogOnMap: boolean;
  setShowMapPanel: React.Dispatch<React.SetStateAction<boolean>>;
  setHoveredListing: React.Dispatch<React.SetStateAction<Listing | undefined>>;
  showMapPanel: boolean;
  t: ReturnType<typeof useAllListingsScreen>['t'];
}) {
  return (
    <View
      onLayout={(event) => {
        const nextHeight = event.nativeEvent.layout.height;
        setTopContentHeight((currentHeight) => (currentHeight === nextHeight ? currentHeight : nextHeight));
      }}
      style={{ position: 'relative', zIndex: 20 }}
    >
      {isGuestWeb || isLoggedIn || isMobileNative ? (
        <DesktopFiltersSubheader
          activeCategory={activeCategory}
          activeTag={activeTag}
          appliedFilters={isLoggedIn ? appliedFilters : undefined}
          currentTab={displayedTab}
          listingType={type}
          navigation={navigation}
          offersTotal={shouldShowMapPanel ? (mapListingCounts[ListingType.offer] ?? offersTotal) : offersTotal}
          onSort={handleSort}
          onTabChange={handleTabChange}
          peopleTotal={displayedPeopleTotal}
          requestsTotal={shouldShowMapPanel ? (mapListingCounts[ListingType.request] ?? requestsTotal) : requestsTotal}
          showPeopleTab={shouldShowPeopleTab}
          sortBy={currentFilters.sortBy}
          sortOptions={sortOptions}
          trailing={
            <MapPanelToggle
              displayedTab={displayedTab}
              canSearchCatalogOnMap={canSearchCatalogOnMap}
              setShowMapPanel={setShowMapPanel}
              setHoveredListing={setHoveredListing}
              showMapPanel={showMapPanel}
              t={t}
            />
          }
        />
      ) : null}

      {isGuestWeb ? <GuestAnnouncementBanner /> : null}
    </View>
  );
}

function MapPanelToggle({
  displayedTab,
  canSearchCatalogOnMap,
  setShowMapPanel,
  setHoveredListing,
  showMapPanel,
  t,
}: {
  displayedTab: AllListingsTab;
  canSearchCatalogOnMap: boolean;
  setShowMapPanel: React.Dispatch<React.SetStateAction<boolean>>;
  setHoveredListing: React.Dispatch<React.SetStateAction<Listing | undefined>>;
  showMapPanel: boolean;
  t: ReturnType<typeof useAllListingsScreen>['t'];
}): React.ReactNode {
  return displayedTab !== peopleTab && canSearchCatalogOnMap ? (
    <Button
      onPress={() => {
        setShowMapPanel((previous) => !previous);
        setHoveredListing(undefined);
      }}
      style={{ borderRadius: 21, height: 42 }}
      variant={showMapPanel ? 'primary' : 'secondaryLight'}
    >
      <IconMap
        color={showMapPanel ? theme.colors.white : theme.colors.black}
        strokeWidth={ICON_STROKE_WIDTH}
        size={24}
        style={{ marginRight: 6 }}
      />
      <Text color={showMapPanel ? 'white' : 'black'} fontSize={15} lineHeight={18} numberOfLines={1}>
        {t('explorationMode.map', { ns: 'all-listings', defaultValue: 'Map' })}
      </Text>
    </Button>
  ) : null;
}

function DesktopFiltersSubheader({
  activeCategory,
  activeTag,
  appliedFilters,
  currentTab,
  listingType,
  navigation,
  offersTotal,
  onSort,
  onTabChange,
  peopleTotal,
  requestsTotal,
  showPeopleTab,
  sortBy,
  sortOptions,
  trailing,
}: DesktopFiltersSubheaderProps) {
  const { tagsEnabled } = useListingCatalog();
  const [showSort, setShowSort] = React.useState(false);
  const categoriesEnabled = useListingCategoriesEnabled();

  return (
    <Row
      flexWrap="wrap"
      style={{
        alignItems: 'center',
        alignSelf: 'center',
        justifyContent: 'flex-start',
        width: '100%',
        maxWidth: CATALOG_GRID_MAX_WIDTH + 2 * theme.spacing['spacing.general.x4'],
        gap: theme.spacing['spacing.general.x2'],
        paddingHorizontal: theme.spacing['spacing.general.x4'],
        paddingVertical: theme.spacing['spacing.general.x2'],
        zIndex: 20,
      }}
    >
      <TabSelector
        currentTab={currentTab}
        onTabChange={onTabChange}
        offersTotal={offersTotal}
        peopleTotal={peopleTotal}
        requestsTotal={requestsTotal}
        showPeopleTab={showPeopleTab}
      />
      <DesktopLocationFilter appliedFilters={appliedFilters} listingType={listingType} navigation={navigation} />
      {categoriesEnabled && <DesktopCategoryFilter activeCategory={activeCategory} navigation={navigation} />}
      {tagsEnabled ? <DesktopTagFilter activeTag={activeTag} navigation={navigation} /> : null}
      {currentTab !== peopleTab ? (
        <SortDropdown
          isOpen={showSort}
          onSort={onSort}
          onToggle={() => setShowSort((open) => !open)}
          sortBy={sortBy}
          sortOptions={sortOptions}
        />
      ) : null}
      {trailing}
    </Row>
  );
}

function useDesktopFilterPopover() {
  const [open, setOpen] = React.useState(false);
  const menuRef = React.useRef(null);
  const close = useCallback(() => setOpen(false), []);

  useClickOutside([menuRef], (clickedOutside) => {
    if (clickedOutside) close();
  });

  const nativeRef = useRnClickOutside(close);

  return {
    close,
    open,
    ref: isMobileNative ? nativeRef : menuRef,
    toggle: () => setOpen((value) => !value),
  };
}

function useGuestLocationFilter(isLoggedIn: boolean) {
  const { t } = useTranslation('all-listings');
  const isGuestWeb = useIsGuestWeb();
  const route = useRoute<'all-listings'>();
  const { data: defaultLocation } = useDefaultListingLocation();
  const { data: guestAddress } = useReverseGeocodedAddress(
    route.params?.searchCenterLatitude ?? defaultLocation?.latitude,
    route.params?.searchCenterLongitude ?? defaultLocation?.longitude,
    isGuestWeb && !isLoggedIn,
  );
  return {
    route,
    guestDistance: route.params?.searchDistance ?? defaultLocation?.searchDistance ?? 0,
    locationLabel: isLoggedIn
      ? t('guest.location')
      : guestAddress?.city || route.params?.searchLocationLabel || t('guest.location'),
  };
}

function DesktopLocationFilter({
  appliedFilters,
  listingType,
  navigation,
}: {
  appliedFilters?: FilterPreferences;
  listingType: ListingType;
  navigation: SpecificNavigationProp<'all-listings'>;
}) {
  const dismissHint = useSetAtom(guestLocationHintDismissedAtom);
  const isGuestWeb = useIsGuestWeb();
  const popover = useDesktopFilterPopover();
  const isLoggedIn = Boolean(appliedFilters);
  const { route, guestDistance, locationLabel } = useGuestLocationFilter(isLoggedIn);
  const distance = appliedFilters?.searchDistanceState[0] ?? guestDistance;
  const activeCount = appliedFilters
    ? appliedFilters.selectedAddresses.length + appliedFilters.selectedGroups.length
    : route.params?.searchCenterLatitude !== undefined && route.params.searchCenterLongitude !== undefined
      ? 1
      : 0;
  const triggerActive = popover.open;
  const triggerColor = triggerActive ? theme.colors['text.inverse.primary'] : theme.colors['text.primary'];
  const triggerLabel = `${locationLabel} · ${String(Math.round(distance / 1000))} km`;

  return (
    <Column innerRef={popover.ref} position="relative" zIndex={500}>
      <LocationFilterTrigger
        popover={{
          ...popover,
          toggle: () => {
            dismissHint(true);
            popover.toggle();
          },
        }}
        triggerActive={triggerActive}
        triggerColor={triggerColor}
        triggerLabel={triggerLabel}
        activeCount={activeCount}
      />

      <GuestLocationHint hidden={!isGuestWeb || isLoggedIn || popover.open || activeCount > 0} />

      {popover.open ? (
        appliedFilters ? (
          <Popper align="left" withScrollableContent>
            <ListingsFiltersPanel
              appliedFilters={appliedFilters}
              defaultDistance={distance}
              hideCategoryAndTagFilters
              listingType={listingType}
              onClose={popover.close}
              open
              showInBottomSheet={false}
            />
          </Popper>
        ) : (
          <Popper align="left" minWidth={377}>
            <Column width={345}>
              <GuestLocationFilterContent navigation={navigation} onApplied={popover.close} />
            </Column>
          </Popper>
        )
      ) : null}
    </Column>
  );
}

function LocationFilterTrigger({
  popover,
  triggerActive,
  triggerColor,
  triggerLabel,
  activeCount,
}: {
  popover: { close: () => void; open: boolean; ref: React.RefObject<View> | React.RefObject<null>; toggle: () => void };
  triggerActive: boolean;
  triggerColor: string;
  triggerLabel: string;
  activeCount: number;
}) {
  return (
    <Column position="relative">
      <Button
        flexDirection="row"
        onPress={popover.toggle}
        style={{
          backgroundColor: triggerActive ? theme.colors['surface.inverse.default'] : theme.colors['surface.default'],
          borderColor: triggerActive ? theme.colors['border.strong'] : theme.colors['border.secondary'],
          borderRadius: 21,
          borderWidth: 1,
          height: 42,
          paddingHorizontal: theme.spacing['spacing.general.x3'],
        }}
        variant="plain"
      >
        <IconMapPin color={triggerColor} strokeWidth={ICON_STROKE_WIDTH} size={20} style={{ marginRight: 8 }} />
        <Text
          color={triggerActive ? 'text.inverse.primary' : 'text.primary'}
          fontSize={14}
          fontWeight="600"
          numberOfLines={1}
        >
          {triggerLabel}
        </Text>
        {popover.open ? (
          <IconChevronUp color={triggerColor} strokeWidth={ICON_STROKE_WIDTH} size={18} style={{ marginLeft: 6 }} />
        ) : (
          <IconChevronDown color={triggerColor} strokeWidth={ICON_STROKE_WIDTH} size={18} style={{ marginLeft: 6 }} />
        )}
      </Button>
      {activeCount > 0 ? <DesktopFilterCountBadge count={activeCount} /> : null}
    </Column>
  );
}

function DesktopCategoryFilter({
  activeCategory,
  navigation,
}: Pick<DesktopFiltersSubheaderProps, 'activeCategory' | 'navigation'>) {
  const { data: categories } = useAllCategories();
  const { t } = useTranslation('all-listings');
  const language = getI18nLanguage();

  return (
    <DesktopChipFilter
      activeId={activeCategory?.id}
      items={categories?.map((category) => ({ id: category.id, label: category.name[language] })) ?? []}
      label={activeCategory?.name[language] ?? t('guest.categories')}
      onApply={(categoryId) => navigation.setParams({ categoryId })}
      activeCount={activeCategory ? 1 : 0}
    />
  );
}

function DesktopTagFilter({ activeTag, navigation }: Pick<DesktopFiltersSubheaderProps, 'activeTag' | 'navigation'>) {
  const { data: tags } = useAllTags(true);
  const { t } = useTranslation('all-listings');

  return (
    <DesktopChipFilter
      activeId={activeTag?.id}
      activeCount={activeTag ? 1 : 0}
      compact
      includeAllOption={false}
      items={tags?.map((tag) => ({ id: tag.id, label: tag.tagName })) ?? []}
      label={t('guest.tags')}
      onApply={(tagId) => navigation.setParams({ tagId })}
    />
  );
}

function DesktopChipFilter({
  activeId,
  activeCount = 0,
  compact = false,
  includeAllOption = true,
  items,
  label,
  onApply,
}: {
  activeId?: string;
  activeCount?: number;
  compact?: boolean;
  includeAllOption?: boolean;
  items: { id: string; label: string }[];
  label: string;
  onApply: (id: string | undefined) => void;
}) {
  const { t } = useTranslation('all-listings');
  const [draftId, setDraftId] = React.useState<string | undefined>(activeId);
  const popover = useDesktopFilterPopover();

  const syncDraftId = useEffectEvent(() => setDraftId(activeId));
  useEffect(() => {
    if (popover.open) return;
    const timeout = setTimeout(syncDraftId, 0);
    return () => clearTimeout(timeout);
  }, [activeId, popover.open]);

  const apply = () => {
    onApply(draftId);
    popover.close();
  };
  const triggerActive = popover.open || activeCount > 0;
  const triggerColor = triggerActive ? theme.colors['text.inverse.primary'] : theme.colors['text.primary'];

  return (
    <Column innerRef={popover.ref} position="relative" zIndex={490}>
      <Column position="relative">
        <Button
          flexDirection="row"
          onPress={() => {
            setDraftId(activeId);
            popover.toggle();
          }}
          style={{
            backgroundColor: triggerActive ? theme.colors['surface.inverse.default'] : theme.colors['surface.default'],
            borderColor: triggerActive ? theme.colors['border.strong'] : theme.colors['border.secondary'],
            borderRadius: 21,
            borderWidth: 1,
            height: 42,
            paddingHorizontal: theme.spacing['spacing.general.x3'],
          }}
          variant="plain"
        >
          <Text
            color={triggerActive ? 'text.inverse.primary' : 'text.primary'}
            fontSize={14}
            fontWeight="600"
            numberOfLines={1}
          >
            {label}
          </Text>
          {popover.open ? (
            <IconChevronUp color={triggerColor} strokeWidth={ICON_STROKE_WIDTH} size={18} style={{ marginLeft: 6 }} />
          ) : (
            <IconChevronDown color={triggerColor} strokeWidth={ICON_STROKE_WIDTH} size={18} style={{ marginLeft: 6 }} />
          )}
        </Button>
        {activeCount > 0 ? <DesktopFilterCountBadge count={activeCount} /> : null}
      </Column>

      {popover.open ? (
        <Popper align="left" minWidth={compact ? 317 : 338}>
          <Column gap="spacing.general.x4" width={compact ? 285 : 306}>
            <ScrollView showsVerticalScrollIndicator style={{ maxHeight: 258 }}>
              <Row flexWrap="wrap" style={{ gap: theme.spacing['spacing.general.x2'] }}>
                {includeAllOption ? (
                  <DesktopFilterChip
                    label={t('filters.all')}
                    onPress={() => setDraftId(undefined)}
                    selected={!draftId}
                  />
                ) : null}
                {items.map((item) => (
                  <DesktopFilterChip
                    key={item.id}
                    label={item.label}
                    onPress={() => setDraftId(item.id)}
                    selected={item.id === draftId}
                  />
                ))}
              </Row>
            </ScrollView>

            <Row alignItems="center" justifyContent="flex-end" gap="spacing.general.x3">
              <Button onPress={() => setDraftId(undefined)} variant="plain">
                <Text fontSize={14} fontWeight="600">
                  {t('guest.reset')}
                </Text>
              </Button>
              <Button onPress={apply} style={{ borderRadius: 22, height: 44, paddingHorizontal: 20 }} variant="primary">
                <Text color="white" fontSize={14} fontWeight="600">
                  {t('guest.apply')}
                </Text>
              </Button>
            </Row>
          </Column>
        </Popper>
      ) : null}
    </Column>
  );
}

function DesktopFilterChip({ label, onPress, selected }: { label: string; onPress: () => void; selected: boolean }) {
  return (
    <Button
      onPress={onPress}
      style={{
        backgroundColor: selected ? theme.colors['surface.inverse.default'] : theme.colors['surface.secondary-neutral'],
        borderRadius: 20,
        minHeight: 32,
        paddingHorizontal: theme.spacing['spacing.general.x2'],
        paddingVertical: theme.spacing['spacing.general.x1'],
      }}
      variant="plain"
    >
      <Text color={selected ? 'white' : 'text.primary'} fontSize={14} fontWeight="600">
        {label}
      </Text>
    </Button>
  );
}

function DesktopFilterCountBadge({ count }: { count: number }) {
  return (
    <Column
      alignItems="center"
      backgroundColor="surface.brand.default"
      borderRadius="radius.full"
      height={16}
      justifyContent="center"
      position="absolute"
      right={theme.spacing['spacing.general.x1']}
      top={-5}
      width={16}
    >
      <Text color="text.inverse.primary" fontSize={10} fontWeight="700">
        {count}
      </Text>
    </Column>
  );
}

export function SearchBar() {
  const [searchQuery, setSearchQuery] = useAtom(searchObjectQuery);
  const navigation = useNavigation();
  const onSubmit = (query: string) => {
    setSearchQuery(query);
    navigation.setParams({ type: ListingType.offer, search: query || undefined });
  };

  const [showSearchBarValue, setShowSearchBar] = useAtom(showSearchBar);

  useFocusEffect(
    React.useCallback(() => {
      if (searchQuery.length > 0) {
        setShowSearchBar(true);
      }
      return () => {
        //
      };
    }, [searchQuery.length, setShowSearchBar]),
  );

  if (!showSearchBarValue) {
    return (
      <Row
        flex={1}
        minWidth={600}
        maxWidth={960}
        justifyContent="center"
        style={{
          marginHorizontal: 'auto',
        }}
      >
        <SearchInput initialValue={searchQuery} onSubmit={onSubmit} backgroundColor={theme.colors['surface.default']} />
      </Row>
    );
  } else {
    return null;
  }
}

export const AccountsBlock = () => {
  const isLoggedIn = useAtomValue(isLoggedInAtom);
  const { t: tBillboard } = useTranslation('billboard');
  const navigation = useNavigation();
  const isGuestWeb = useIsGuestWeb();
  const showSignup = useSetAtom(presentGuestSignupPromptAtom);

  if (isLoggedIn) {
    return null;
  }

  return (
    <Row
      padding="spacing.general.x8"
      backgroundColor="surface.tertiary"
      borderRadius="radius.lg"
      marginBottom="spacing.general.x8"
    >
      <Column
        flex={1}
        alignItems="center"
        justifyContent="center"
        gap="spacing.general.x4"
        paddingVertical="spacing.general.x4"
      >
        <Column>
          <Text variant="bodyLarge" color="text.primary" fontWeight="700" textAlign="center" fontSize={24}>
            {tBillboard('unauthenticatedTitle')}
          </Text>
          <Text
            variant="bodyLarge"
            color="text.primary"
            fontWeight="700"
            textAlign="center"
            fontSize={24}
            marginBottom="spacing.general.x4"
          >
            {tBillboard('unauthenticatedTitle_2')}
          </Text>
        </Column>

        {isGuestWeb ? (
          <Button variant="secondary" onPress={() => showSignup(true)}>
            {tBillboard('signUpOrLogin', { ns: 'login' })}
          </Button>
        ) : (
          <Row gap="spacing.general.x8">
            <Button
              variant="primary"
              paddingHorizontal="spacing.general.x8"
              paddingVertical="spacing.general.x4"
              style={{ borderRadius: 999 }}
              onPress={() => {
                navigation.navigate('onboardingIntro', { login: false }, { pop: true });
              }}
            >
              <Text fontWeight="bold" color="text.inverse.primary">
                {tBillboard('buttonSignUp')}
              </Text>
            </Button>

            <Button
              variant="plain"
              paddingHorizontal="spacing.general.x8"
              paddingVertical="spacing.general.x4"
              style={{ borderRadius: 999, borderWidth: 1, borderColor: 'text.inverse.primary' }}
              onPress={() => {
                navigation.navigate('onboardingIntro', { login: true }, { pop: true });
              }}
            >
              <Text fontWeight="bold" color="text.primary">
                {tBillboard('buttonLogIn')}
              </Text>
            </Button>
          </Row>
        )}
      </Column>
    </Row>
  );
};
